import { useState } from "react";
import { useParams } from "react-router-dom";
import { useSubmissionDetailQuery } from "./useSubmissionQueries";
import { useAppScope } from "../../../shared";
import {
  exportSubmissionPdfApi,
  exportSubmissionExcelApi,
} from "../api/submissionsApi";
import { downloadFile, errorMessage, printFile } from "../../../utils";

export const useSubmissionsDetails = () => {
  const { id } = useParams();
  const { submissionDetailQuery, moveToDraftMutation } =
    useSubmissionDetailQuery(id);
  const { globalData } = useAppScope();

  const stockTakeStatuses = globalData?.stocktake_statuses || [];

  // Export menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const openExportMenu = Boolean(anchorEl);

  // Move to draft dialog state
  const [openMoveToDraftDialog, setOpenMoveToDraftDialog] = useState(false);

  const handleMoveToDraftClick = () => setOpenMoveToDraftDialog(true);
  const handleMoveToDraftCancel = () => setOpenMoveToDraftDialog(false);
  const handleMoveToDraftConfirm = () => {
    handleMoveToDraftCancel();
    moveToDraftMutation.mutate(undefined, {
      onSuccess: () => setOpenMoveToDraftDialog(false),
    });
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setAnchorEl(null);
  };

  const handlePrint = async () => {
    try {
      const response = await exportSubmissionPdfApi(id);
      printFile(response?.data);
    } catch {
      errorMessage("Failed to load PDF for printing");
    }
  };

  const handleExportPDF = async () => {
    handleExportClose();
    try {
      const response = await exportSubmissionPdfApi(id);
      downloadFile({ response, fileName: `submission-${id}.pdf` });
    } catch {
      errorMessage("Failed to export PDF");
    }
  };

  const handleExportExcel = async () => {
    handleExportClose();
    try {
      const response = await exportSubmissionExcelApi(id);
      downloadFile({ response, fileName: `submission-${id}.xlsx` });
    } catch {
      errorMessage("Failed to export Excel");
    }
  };

  const submission = submissionDetailQuery.data;
  const submissionItems = submission?.items
    ? submission?.items.map((item) => ({
        ...item,
        stock_on_hand: Number(item.stock_on_hand).toFixed(2),
        amount: item.amount.toFixed(2),
      }))
    : [];

  // Group items: category_id → subcategory_id → items[], with display orders for sorting
  const groupedByCategory = submissionItems.reduce((acc, item) => {
    const catId = item.category_id;
    const subId = item.subcategory_id;
    if (!acc[catId])
      acc[catId] = {
        name: item.category_name,
        display_order: item.category_display_order,
        subcategories: {},
      };
    if (!acc[catId].subcategories[subId])
      acc[catId].subcategories[subId] = {
        name: item.subcategory_name,
        display_order: item.subcategory_display_order,
        items: [],
      };
    acc[catId].subcategories[subId].items.push(item);
    return acc;
  }, {});

  // Convert to sorted arrays — plain objects with numeric keys ignore insertion order
  const itemsByCategoryAndSubcategory = Object.entries(groupedByCategory)
    .sort(([, a], [, b]) => a.display_order - b.display_order)
    .map(([catId, { name, subcategories }]) => ({
      id: catId,
      name,
      subcategories: Object.entries(subcategories)
        .sort(([, a], [, b]) => a.display_order - b.display_order)
        .map(([subId, { name: subName, items }]) => ({
          id: subId,
          name: subName,
          items,
        })),
    }));

  // Resolve status label from globalData
  const statusLabel = stockTakeStatuses.find(
    (s) => s.value === submission?.status,
  )?.label;

  const statusColor = submission?.status === 3 ? "primary" : "default";

  const showMoveToDraft = submission?.status === 3 && submission?.is_draftable;

  return {
    id,
    submission,
    itemsByCategoryAndSubcategory,
    statusLabel,
    statusColor,
    showMoveToDraft,
    openMoveToDraftDialog,
    handleMoveToDraftClick,
    handleMoveToDraftCancel,
    handleMoveToDraftConfirm,
    isMovingToDraft: moveToDraftMutation.isPending,
    isLoading: submissionDetailQuery.isLoading,
    anchorEl,
    openExportMenu,
    handleExportClick,
    handleExportClose,
    handlePrint,
    handleExportPDF,
    handleExportExcel,
  };
};
